import * as React from "react";
import Backdrop from "@mui/material/Backdrop";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Fade from "@mui/material/Fade";
import styled from "styled-components";
import { Flex, Button, ButtonClass, Loading } from "src/components/ui";
import closeIcon from "src/assets/images/common/closeIcon.svg";
import { theme } from "src/styles/Theme";
import { toast, ToastContainer } from "react-toastify";
import userServices from "src/service/userServices";
import { ArtisansServices } from "src/service/ArtisansServices";
import { auth } from "src/store/Auth";

const Wrapper = styled.div`
  .heading {
    background-color: ${(props) => props.theme.colors.gray_04};
    padding: 10px 20px;
    height: 56px;
    align-items: center;
    display: flex;
    border-radius: 8px 8px 0 0;

    img {
      padding: 8px;
      &:hover {
        cursor: pointer;
        background-color: ${(props) => props.theme.colors.lilac};
        transition: 0.3s;
        border-radius: 50%;
      }
    }

    p {
      font-weight: 700;
      font-size: 16px;
      color: ${(props) => props.theme.colors.text_01};
    }
  }

  .content {
    border-radius: 0 0 8px 8px;
    padding: 20px;

    .label {
      font-size: 14px;
      color: ${(props) => props.theme.colors.text_01};
      margin-bottom: 5px;
    }

    input {
      padding: 10px 14px;
      border: 1px solid ${(props) => props.theme.colors.text_02};
      background-color: ${(props) => props.theme.colors.gray_04};
      color: ${(props) => props.theme.colors.text_01};
      font-size: 16px;
      border-radius: 8px;
      width: 100%;
      height: 44px;
      outline: none;
      margin-bottom: 15px;

      &:focus {
        border: 1px solid ${(props) => props.theme.colors.purple};
      }
    }

    .button {
      padding: 8px 15px;
      background-color: ${(props) => props.theme.colors.gray_04};
      border-radius: 8px;
    }
  }
`;

const style = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 532,
  maxWidth: "90%",
  bgcolor: theme.colors.white,
  borderRadius: "8px",
  boxShadow: 24,
  outline: "none",
};

export const InitiateWithdrawl: React.FC<{
  open: boolean;
  handleClose: () => void;
  partnerId: string;
  setOtpModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({ open, handleClose, partnerId, setOtpModalOpen }) => {
  const [isLoading, setIsLoading] = React.useState(false);

  const [withdrawalPayload, setWithdrawalPayload] = React.useState({
    amount: "",
    narration: "",
  });

  const { authUser } = auth.use();

  const isDisabled = !withdrawalPayload.amount || !withdrawalPayload.narration;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setWithdrawalPayload({
      ...withdrawalPayload,
      [e.target.name]: e.target.value,
    });
  };

  const submitHandler = (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    const payload = {
      amount: Number(withdrawalPayload.amount),
      narration: withdrawalPayload.narration,
      id: partnerId,
    };

    const request = ArtisansServices.initiateWithdrawalAdmin(payload);

    request
      .then((res: any) => {
        toast.success(res?.data?.message || "Withdrawal initiated");
        handleClose();
        setOtpModalOpen(true)
      })
      .catch((err: any) => {
        toast.error(err?.response?.data?.error?.message || "Something went wrong");
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <>
      <ToastContainer />
      <Modal
        open={open}
        onClose={handleClose}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 500 }}
      >
        <Fade in={open}>
          <Box sx={style}>
            <Wrapper>
              <div className="heading">
                <Flex
                  justify="space-between"
                  align="center"
                  style={{ width: "100%" }}
                >
                  <p>Initiate Withdrawal</p>
                  <img src={closeIcon} alt="close" onClick={handleClose} />
                </Flex>
              </div>

              <div className="content">
                <form onSubmit={submitHandler}>
                  <Flex direction="column">
                    <div>
                      <p className="label">Amount (Naira)</p>
                      <input
                        type="number"
                        name="amount"
                        value={withdrawalPayload.amount}
                        onChange={handleChange}
                        placeholder="Enter amount"
                      />
                    </div>

                    <div>
                      <p className="label">Narration</p>
                      <input
                        type="text"
                        name="narration"
                        value={withdrawalPayload.narration}
                        onChange={handleChange}
                        placeholder="Enter narration"
                      />
                    </div>

                    <Flex justify="space-between">
                      <div className="button">
                        <Button
                          type="submit"
                          classes={[ButtonClass.SOLID]}
                          disabled={isDisabled || isLoading}
                          style={{
                            backgroundColor: theme.colors.purple,
                            minWidth: "120px",
                            display: "flex",
                            justifyContent: "center",
                          }}
                        >
                          {isLoading ? <Loading color="white" /> : "Submit"}
                        </Button>
                      </div>
                    </Flex>
                  </Flex>
                </form>
              </div>
            </Wrapper>
          </Box>
        </Fade>
      </Modal>
    </>
  );
};

export default InitiateWithdrawl;
